<template>
  <b-overlay :show="isLoading || selectAllTradiesBusy" rounded="sm">
    <template #overlay>
      <div class="text-center">
        <digger-loader />
      </div>
    </template>
    <transition name="bounce">
      <b-container fluid>
        <b-row align-h="between" align-v="center">
          <b-col cols="12" md="5" lg="4" class="p-3 mb-0">
            <b-form-input
              v-model="search"
              placeholder="Search name, email, trade, phone..."
              debounce="500"
            />
          </b-col>

          <!-- Desktop buttons: original style, right-aligned (hidden on mobile) -->
          <b-col cols="7" class="d-none d-md-block">
            <b-row align-h="end">
              <b-button
                v-if="canAddTradie"
                variant="primary"
                class="pl-2 pr-3 mr-3"
                @click="addingTradie(true)"
              >
                <b-icon-plus
                  font-scale="1.5"
                  class="align-middle"
                ></b-icon-plus>
                <span class="align-middle">Add/Register Tradie</span>
              </b-button>
              <b-button
                v-if="canAddTradie"
                variant="success"
                class="pl-2 pr-3 mr-3"
                @click="importingTradieCSV(true)"
              >
                <b-icon-plus
                  font-scale="1.5"
                  class="align-middle"
                ></b-icon-plus>
                <span class="align-middle">Import Tradies</span>
              </b-button>
              <b-button
                v-if="canShareTradie"
                class="pl-2 pr-3 mr-3"
                @click="shareSelectedTradies(true)"
                :disabled="!isAnyTradieSelected"
              >
                <b-icon-plus
                  font-scale="1.5"
                  class="align-middle"
                ></b-icon-plus>
                <span class="align-middle">Share Tradies</span>
              </b-button>
            </b-row>
          </b-col>

          <!-- Mobile buttons: compact icon-only (hidden on desktop) -->
          <b-col
            cols="12"
            class="d-flex d-md-none flex-wrap align-items-center tradie-btn-row px-3 pb-2"
          >
            <b-button
              v-if="canAddTradie"
              variant="primary"
              size="sm"
              class="tradie-action-btn"
              @click="addingTradie(true)"
            >
              <b-icon-plus class="align-middle" />
            </b-button>
            <b-button
              v-if="canAddTradie"
              variant="success"
              size="sm"
              class="tradie-action-btn"
              @click="importingTradieCSV(true)"
            >
              <b-icon icon="upload" class="align-middle" />
            </b-button>
            <b-button
              v-if="canShareTradie"
              variant="outline-primary"
              size="sm"
              class="tradie-action-btn"
              @click="shareSelectedTradies(true)"
              :disabled="!isAnyTradieSelected"
            >
              <b-icon icon="share" class="align-middle" />
            </b-button>
          </b-col>
        </b-row>

        <!-- Add Tradie -->
        <b-modal
          v-model="isAddingTradie"
          title="Add Tradie to Company"
          modal-class="add-tradie-modal"
          centered
          :hide-footer="true"
        >
          <b-overlay :show="isLoading" rounded="sm">
            <template #overlay>
              <div class="text-center">
                <digger-loader />
              </div>
            </template>
            <b-form @submit.prevent="onAddTradie">
              <b-row>
                <b-col class="add-tradie-modal-form-col">
                  <b-form-group label="Tradie Email Address">
                    <b-form-tags
                      v-model="tradieEmail"
                      @keydown.enter.prevent="tradieEmail"
                      placeholder="Enter Email Address"
                      separator=",; "
                      required
                      remove-on-delete
                      class="add-tradie-modal-tags"
                    ></b-form-tags>
                    <div class="input-error" v-if="errors">
                      {{ errors.email[0] }}
                    </div>
                  </b-form-group>
                </b-col>
              </b-row>

              <b-row align-h="end" class="p-2 add-tradie-modal-footer">
                <b-button
                  variant="outline-secondary"
                  class="mx-2 add-tradie-modal-btn"
                  @click="addingTradie(false)"
                >
                  CANCEL
                </b-button>
                <b-button
                  type="submit"
                  variant="primary"
                  class="mx-2 add-tradie-modal-btn"
                >
                  ADD TRADIE
                </b-button>
              </b-row>
            </b-form>
          </b-overlay>
        </b-modal>

        <!-- Import tradies (CSV or Excel) -->
        <b-modal
          v-model="isImportingTradieCSV"
          title="Import tradies"
          modal-class="import-tradie-modal"
          centered
          :hide-footer="true"
        >
          <b-overlay :show="isLoading" rounded="sm">
            <template #overlay>
              <div class="text-center">
                <digger-loader />
              </div>
            </template>
            <b-form @submit.prevent="onImportTradieCSV">
              <b-form-row>
                <b-col>
                  <b-form-group label="CSV or Excel file (.csv, .xlsx)">
                    <b-form-file
                      v-model="tradieCsvFile"
                      :accept="tradieImportAccept"
                      class="excluded"
                      required
                      @change="onTradieImportFileChange"
                    />
                    <p
                      v-if="tradieImportFileClientHint"
                      class="text-danger small mb-1"
                    >
                      {{ tradieImportFileClientHint }}
                    </p>
                    <p class="text-black opacity-100">
                      Download:
                      <a href="/tradiepass_tradies.csv">
                        Tradies template (CSV)
                      </a>
                    </p>
                  </b-form-group>
                </b-col>
              </b-form-row>

              <b-row align-h="end" class="p-2 import-tradie-modal-footer">
                <b-button
                  variant="outline-secondary"
                  class="mx-2 import-tradie-modal-btn"
                  @click="importingTradieCSV(false)"
                >
                  CANCEL
                </b-button>
                <b-button
                  type="submit"
                  variant="primary"
                  class="mx-2 import-tradie-modal-btn"
                >
                  UPLOAD
                </b-button>
              </b-row>
            </b-form>
          </b-overlay>
        </b-modal>

        <TradieCsvImportErrorModal
          v-model="showImportCsvErrorModal"
          :variant="importCsvErrorModalVariant"
          :summary="importCsvErrorSummary"
          :emails="importCsvErrorEmails"
          :skipped-already-in-company="importCsvErrorSkippedAlreadyInCompany"
          :skipped-business-admins="importCsvErrorSkippedBusinessAdmins"
          :imported-emails="importCsvErrorImportedEmails"
          :invited-emails="importCsvErrorInvitedEmails"
        />

        <!-- Share Tradies -->
        <ShareTradiesModal
          v-model="isShareSelectedTradies"
          :selected-tradies="selectedTradies"
        />

        <!-- Tokens Modal -->
        <purchase-token-modal
          v-if="canPurchaseTokens"
          :showModal="isPurchasingToken"
          @closeModal="purchasingToken(false)"
          @purchase="onPurchaseTokens"
        />

        <div class="table-wrapper" ref="tableWrapper">
          <b-tabs v-model="activeTab">
            <b-tab
              title="Active"
              id="active"
              border-variant="light"
              bg-variant="light"
              body-class="px-0 py-0"
            >
            </b-tab>
            <b-tab
              title="Archived"
              id="archived"
              border-variant="light"
              bg-variant="light"
              body-class="px-0 py-0"
            >
            </b-tab>
          </b-tabs>

          <!-- Mobile: compact tradie rows (hidden on md+) -->
          <div class="d-md-none">
            <div
              v-for="item in tradies"
              :key="item.id"
              class="mobile-tradie-item c-pointer"
              :class="{ 'mobile-tradie-item--locked': !item.can_access }"
              @click="viewTradie(item)"
            >
              <div class="d-flex align-items-center">
                <div class="flex-grow-1" style="min-width: 0">
                  <div class="d-flex align-items-center">
                    <span
                      class="mobile-tradie__name"
                      :class="{ 'text-muted': !item.can_access }"
                    >
                      {{ item.full_name || item.email || '-' }}
                    </span>
                    <b-icon
                      v-if="!item.can_access"
                      icon="lock-fill"
                      class="mobile-tradie__lock-icon ml-1"
                    />
                  </div>
                  <div class="mobile-tradie__meta">
                    <template v-if="item.can_access">
                      {{ item.email
                      }}{{
                        item.trade ? ' · ' + shortenString(item.trade, 20) : ''
                      }}
                    </template>
                    <template v-else>Locked - Assign token to view</template>
                  </div>
                </div>
                <div
                  class="d-flex align-items-center mobile-tradie__actions"
                  @click.stop
                >
                  <b-button
                    v-if="isPendingInvitation(item)"
                    :key="`resend-mobile-${item.id}-${item.invite_resent_at}`"
                    v-b-tooltip.hover
                    :title="resendInviteTooltip(item)"
                    :variant="hasBeenResent(item) ? 'secondary' : 'warning'"
                    class="mobile-tradie__action-btn"
                    @click.stop="resendInvitationDirectly(item)"
                    :disabled="isLoading"
                  >
                    <b-icon icon="arrow-repeat" />
                  </b-button>
                  <b-button
                    v-else-if="isTradieSelectable(item)"
                    :style="{
                      backgroundColor: isTradieSelected(item.id)
                        ? '#28a745'
                        : 'white',
                    }"
                    class="mobile-tradie__action-btn mobile-tradie__select-btn"
                    @click.stop="toggleTradieSelection(item.id)"
                  >
                    <b-icon-check2 v-if="isTradieSelected(item.id)" />
                  </b-button>
                  <span
                    v-else
                    class="mobile-tradie__action-btn text-muted d-inline-flex align-items-center justify-content-center"
                    >-</span
                  >
                  <b-button
                    v-if="canRemoveTradie"
                    variant="outline-light"
                    class="mobile-tradie__action-btn"
                    @click.stop="removingTradie(true, item.id)"
                  >
                    <b-icon-x-circle-fill variant="danger" />
                  </b-button>
                  <b-button
                    v-if="canArchiveTradie"
                    class="mobile-tradie__action-btn"
                    :variant="activeTab === 1 ? 'success' : 'danger'"
                    @click.stop="archive(item)"
                  >
                    <b-icon
                      :icon="activeTab === 1 ? 'archive-fill' : 'archive'"
                    />
                  </b-button>
                </div>
              </div>
            </div>
            <div
              v-if="!tradies || !tradies.length"
              class="text-center py-4 text-muted"
            >
              No tradies found.
            </div>
          </div>

          <!-- Desktop: full table (hidden below md) -->
          <div class="d-none d-md-block">
            <b-table
              id="my-table"
              class="mt-4 tradies-table"
              responsive
              hover
              show-empty
              ref="tradiesTable"
              :fields="computedFields"
              :items="tradies"
              :tbody-tr-class="getRowClass"
              @sort-changed="onSortChanged"
              @row-clicked="viewTradie"
            >
              <template #table-busy>
                <div class="text-center text-primary my-2">
                  <digger-loader />
                </div>
              </template>

              <template #cell(full_name)="{ item }">
                <div class="cell-content-wrapper">
                  <span :class="{ 'text-muted': !item.can_access }">
                    {{
                      item.full_name == null ||
                      String(item.full_name).trim() === ''
                        ? '-'
                        : item.full_name
                    }}
                  </span>
                  <div
                    v-if="!item.can_access"
                    class="lock-overlay"
                    :ref="`lock-overlay-${item.id}`"
                  >
                    <img
                      src="@/assets/images/lock.png"
                      alt="Locked"
                      class="lock-icon mr-2"
                    />
                    <span>Locked - Assign token to view</span>
                  </div>
                </div>
              </template>

              <!-- <template #cell(email)="{ item }">
              <span v-if="item.can_access">{{ item.email }}</span>
            </template> -->

              <template #cell(trade)="{ item }">
                <span
                  v-if="item.can_access"
                  v-b-tooltip.html
                  :title="getTooltipText(item.trade)"
                  >{{ shortenString(item.trade) }}</span
                >
              </template>

              <template #cell(phone_number)="{ item }">
                <span v-if="item.can_access">{{
                  item.phone_number === null ? '-' : item.phone_number
                }}</span>
              </template>

              <template #cell(project)="{ item, value }">
                <span
                  v-if="item.can_access"
                  :class="{ 'text-noproject': value.length === 0 }"
                >
                  {{ value.length === 0 ? 'No Project' : value.join(' | ') }}
                </span>
              </template>

              <template #cell(remove)="{ item }">
                <b-button
                  variant="outline-light"
                  style="height: 32px"
                  class="px-2"
                  @click="removingTradie(true, item.id)"
                >
                  <b-icon-x-circle-fill variant="danger" />
                </b-button>
              </template>

              <template #head(select)>
                <span
                  v-if="hasSelectableTradiesOnPage"
                  class="text-center d-block"
                  :style="{
                    cursor: selectAllTradiesBusy ? 'wait' : 'pointer',
                    'user-select': 'none',
                    opacity: selectAllTradiesBusy ? 0.5 : 1,
                  }"
                  :title="selectAllTradiesHeaderTitle"
                  @click.stop="!selectAllTradiesBusy && selectAllTradies()"
                >
                  {{ selectAllTradiesHeaderLabel }}
                </span>
                <span v-else class="text-center d-block">Select</span>
              </template>

              <template #cell(select)="{ item }">
                <div
                  v-if="isPendingInvitation(item)"
                  class="text-center resend-invitation-container"
                >
                  <b-button
                    :key="`resend-desktop-${item.id}-${item.invite_resent_at}`"
                    v-b-tooltip.hover
                    :title="resendInviteTooltip(item)"
                    :variant="hasBeenResent(item) ? 'secondary' : 'warning'"
                    size="sm"
                    class="px-2 resend-invitation-btn"
                    style="
                      height: 34px;
                      font-size: 0.75rem;
                      padding: 0.2rem 0.4rem;
                      white-space: nowrap;
                      align-items: center;
                      display: inline-flex;
                      justify-content: center;
                    "
                    @click.stop="resendInvitationDirectly(item)"
                    :disabled="isLoading"
                  >
                    Resend Invitation
                  </b-button>
                </div>
                <div v-else-if="isTradieSelectable(item)" class="text-center">
                  <b-button
                    :style="{
                      backgroundColor: isTradieSelected(item.id)
                        ? '#28a745'
                        : 'white',
                    }"
                    style="
                      height: 32px;
                      width: 32px;
                      border-radius: 8px;
                      border: 1px solid grey;
                      padding-left: 5px !important;
                    "
                    class="px-2"
                    @click.stop="toggleTradieSelection(item.id)"
                  >
                    <b-icon-check2 v-if="isTradieSelected(item.id)" />
                  </b-button>
                </div>

                <span v-else class="text-muted">-</span>
              </template>
              <template #head(archive)>
                <span class="text-center d-block">
                  {{ activeTab === 1 ? 'Unarchive' : 'Archive' }}</span
                >
              </template>
              <template #cell(archive)="{ item }">
                <div class="text-center">
                  <b-button
                    size="sm"
                    style="
                      height: 34px;
                      padding: 0.2rem 0.6rem;
                      align-items: center;
                      display: inline-flex;
                      justify-content: center;
                    "
                    :variant="activeTab === 1 ? 'success' : 'danger'"
                    @click="archive(item)"
                  >
                    <b-icon
                      :icon="activeTab === 1 ? 'archive-fill' : 'archive'"
                    >
                    </b-icon>
                  </b-button>
                </div>
              </template>
              <template #cell(token_expiry)="{ item }">
                <div v-if="item.can_access">
                  <span v-if="tokenExpiryDates[item.id]">
                    <span
                      :class="{
                        'text-danger font-weight-bold': isExpiringSoon(
                          tokenExpiryDates[item.id],
                          30,
                        ),
                        'text-warning':
                          isExpiringSoon(tokenExpiryDates[item.id], 60) &&
                          !isExpiringSoon(tokenExpiryDates[item.id], 30),
                      }"
                    >
                      {{ formatExpiryDate(tokenExpiryDates[item.id]) }}
                    </span>
                  </span>
                  <span
                    v-else-if="tokenExpiryDates[item.id] === null"
                    class="text-muted"
                    style="font-size: 0.85rem"
                    >-</span
                  >
                  <span v-else class="text-muted" style="font-size: 0.85rem"
                    >Loading...</span
                  >
                </div>
                <span v-else-if="!item.can_access">-</span>
              </template>
            </b-table>
          </div>
        </div>
        <!-- Assign modal -->
        <assign-token-modal
          v-if="isAssigningToken && canAssignTokens"
          :showModal="isAssigningToken"
          :tradieToAssign="tradieToAssign"
          @closeModal="onAssignModalClose"
          @assign="onAssignToken"
          @purchaseTokens="onAssignModalPurchaseTokens"
        />
        <mass-notification-modal
          ref="massNotificationModal"
          v-model="isShareMassNotification"
          @sent="onMassNotificationSent"
        />

        <!-- Message Dialog -->
        <b-modal v-model="showDeclinedModal" centered size="lg">
          <template #modal-title>
            <h3 class="font-weight-bold">
              Trying to view this tradies profile?
            </h3>
          </template>

          <!-- prettier-ignore -->
          <b-row class="mb-4">
            <b-col>
              You're unable to view this tradie's profile because they haven't accepted your invitation yet<br />
              - or they may have declined it.
              <br /><br />
              To proceed, you can:
              <ul>
                <li>Ask the tradie to accept your invite, or</li>
                <li>Resend the invitation from your dashboard.</li>
              </ul>
              If the tradie has already accepted and you're still having issues, contact
              <a href="mailto:support@tradiepass.com.au">support@tradiepass.com.au</a>.
            </b-col>
          </b-row>

          <template #modal-footer>
            <div class="d-flex" style="gap: 16px">
              <b-button variant="primary" @click="showDeclinedModal = false">
                OK
              </b-button>
            </div>
          </template>
        </b-modal>

        <!-- Remove Tradie -->
        <b-modal
          v-model="isRemovingTradie"
          title="Remove Tradie from this Company"
          modal-class="remove-tradie-modal"
          centered
        >
          <p class="my-4 remove-tradie-modal-text">
            Are you sure to remove <b>"{{ tradieName }}"</b>?<br />
            <span class="text-danger">
              This will remove tradie from all Projects
            </span>
          </p>

          <template #modal-footer>
            <b-button
              variant="secondary"
              class="remove-tradie-modal-btn"
              @click="removingTradie(false)"
            >
              CANCEL
            </b-button>
            <b-button
              variant="primary"
              class="remove-tradie-modal-btn"
              @click="onRemoveTradie()"
            >
              YES
            </b-button>
          </template>
        </b-modal>

        <b-pagination
          v-if="rows > perPage"
          v-model="currentPage"
          :total-rows="rows"
          :per-page="perPage"
          :current-page="currentPage"
          aria-controls="my-table"
          align="center"
          class="mt-3 mt-md-0 tradie-pagination"
        />
        <p v-if="tradiesMeta" align="center" class="tradie-count-label mt-3">
          Showing {{ tradies.length }} out of total of
          {{ tradiesMeta.total }} tradies
        </p>
      </b-container>
    </transition>
  </b-overlay>
</template>

<script>
import { canBuyTokens } from '@/utils/tokenAccess'
import dayjs from 'dayjs'
import { mapGetters, mapActions, mapState } from 'vuex'
import PurchaseTokenModal from '@/components/modals/PurchaseTokenModal'
import AssignTokenModal from '@/components/modals/AssignTokenModal'
import MassNotificationModal from '@/components/modals/MassNotificationModal'
import { applyTokenExpiryFromListTradies } from '@/utils/tradieListToken'
import {
  ADD_TRADIE_INVITE_ONLY_MESSAGE,
  ADD_TRADIE_NEW_ACCOUNT_MESSAGE,
  CSV_IMPORT_MSG_INVITES_ONLY,
} from '@/constants/companyTradieMessages'
import {
  getTradieImportCsvFileValidationMessage,
  isTradieImportCsvFileMimeValidationMessage,
  parseTradieCsvImport422,
} from '@/utils/tradieCsvImportErrors'
import {
  TRADIE_IMPORT_ACCEPT,
  TRADIE_IMPORT_INVALID_TYPE_MESSAGE,
  validateTradieImportFile,
} from '@/utils/tradieImportFileValidation'
import TradieCsvImportErrorModal from '@/components/modals/TradieCsvImportErrorModal'

export default {
  components: {
    PurchaseTokenModal,
    AssignTokenModal,
    MassNotificationModal,
    TradieCsvImportErrorModal,
    ShareTradiesModal: () =>
      import(
        /* webpackChunkName: "share-tradies-modal" */
        /* webpackPrefetch: true */
        '@/components/modals/ShareTradiesModal.vue'
      ),
  },
  data() {
    return {
      perPage: 10,
      currentPage: 1,
      search: '',
      sortBy: null,
      sortDir: null,

      tokenQuantity: null,
      isPurchasingToken: false,
      isAssigningToken: false,
      tradieToAssign: null,

      isLoading: false,
      BalanceLoading: false,
      isAddingTradie: false,
      isImportingTradieCSV: false,
      showImportCsvErrorModal: false,
      importCsvErrorModalVariant: 'failure',
      importCsvErrorSummary: '',
      importCsvErrorEmails: [],
      importCsvErrorSkippedAlreadyInCompany: [],
      importCsvErrorSkippedBusinessAdmins: [],
      importCsvErrorImportedEmails: [],
      importCsvErrorInvitedEmails: [],
      isShareSelectedTradies: false,

      isRemovingTradie: false,
      showDeclinedModal: false,

      activeTab: 0,

      tradieId: null,
      tradieName: null,
      tradieEmail: [],
      tradieCsvFile: null,
      tradieImportFileClientHint: null,
      tradieImportAccept: TRADIE_IMPORT_ACCEPT,
      errors: null,

      selectedTradies: [],
      selectAllTradiesBusy: false,
      tokenExpiryDates: {},

      // Mass notification modal
      isShareMassNotification: false,
      massNotificationTab: 'byProject', // 'byProject' | 'byIndividuals'
      massNotificationProjectIds: [], // for "by project" (single: [id], multi: [id1, id2])
      massNotificationSelectedUserIds: [], // for "by individuals"
      massNotificationProjectTradies: [], // tradies for selected project (individuals)
      massNotificationProjectTradiesMeta: null,
      massNotificationTitle: '',
      massNotificationBody: '',
      massNotificationSending: false,
      massNotificationError: null,

      fields: [
        // {
        //   key: 'added',
        //   label: 'Added',
        //   sortable: true,
        //   formatter: (value) => {
        //     return (
        //       new Intl.DateTimeFormat('en-US', {
        //         year: 'numeric',
        //         month: 'short',
        //         day: 'numeric',
        //       }).format(new Date(value)) +
        //       ' at ' +
        //       new Intl.DateTimeFormat('en-US', {
        //         hour: 'numeric',
        //         minute: 'numeric',
        //         hour12: true,
        //       })
        //         .format(new Date(value))
        //         .replace(/(\d+) (am|pm)/i, '$1$2')
        //         .toLowerCase()
        //     )
        //   },
        // },
        {
          key: 'full_name',
          label: 'Name',
          sortable: true,
          formatter: (value) =>
            value == null || String(value).trim() === '' ? '-' : value,
        },

        {
          key: 'email',
          label: 'Email',
          sortable: true,
        },

        {
          key: 'trade',
          label: 'Trade',
          sortable: true,
        },
        {
          key: 'phone_number',
          label: 'Phone Number',
          sortable: true,
          formatter: (value) => (value === null ? '-' : value),
        },
        {
          key: 'project',
          label: 'Project',
          formatter: (value) => (value === null ? ['-'] : value),
        },
        {
          key: 'token_expiry',
          label: 'Token Expiry',
          sortable: false,
        },
        // {
        //   key: 'onboarded',
        //   label: 'Onboarded',
        //   formatter: (value) => (value === null ? '-' : value ? 'Yes' : 'No'),
        // },
        // {
        // key: 'accepted',
        // label: 'Invite',
        // formatter: (value) => {
        //   if (value === null) return 'Pending'
        //   return value ? 'Accepted' : 'Declined'
        // },
        // },

        // {
        //   key: 'joined',
        //   label: 'Joined',
        //   sortable: true,
        //   formatter: (value) => {
        //     return value
        //       ? new Intl.DateTimeFormat('en-US', {
        //         year: 'numeric',
        //         month: 'short',
        //         day: 'numeric',
        //       }).format(new Date(value)) +
        //       ' at ' +
        //       new Intl.DateTimeFormat('en-US', {
        //         hour: 'numeric',
        //         minute: 'numeric',
        //         hour12: true,
        //       })
        //         .format(new Date(value))
        //         .replace(/(\d+) (am|pm)/i, '$1$2')
        //         .toLowerCase()
        //       : '-'
        //   },
        // },
        'remove',
        {
          key: 'select',
          label: 'Select',
          thClass: 'text-center company-tradie-col-select',
          tdClass: 'text-center company-tradie-col-select',
        },
        'archive',
        // {
        //   key: 'assigned',
        //   label: 'Assigned',
        //   formatter: (value) => {
        //     if (value === null) return 'No'
        //     return value ? 'Yes' : 'No'
        //   },
        // },
      ],
    }
  },

  computed: {
    ...mapGetters('company', ['tradies', 'tradiesMeta', 'tradie', 'files']),
    ...mapGetters('token', ['balance', 'balanceLoading', 'canAccess']),
    ...mapGetters('auth', ['user', 'hasPermission']),
    ...mapState('project', { projects: 'projects' }),

    // RBAC gates for tradie management (business-admin-only screen).
    canAddTradie() {
      return this.hasPermission('tradies.create')
    },
    /**
     * Free tier / still inside the 30-day trial — `is_free` on /auth/me, the
     * same flag the router and every token modal gate on. These companies
     * skip the token economy, so token-only UI like the Token Expiry column
     * is hidden for them.
     */
    isFree() {
      return !!(this.user && this.user.is_free)
    },
    canShareTradie() {
      // Sharing exports the tradie's files, so it requires view access to them
      // in addition to the share permission.
      return (
        this.hasPermission('tradies.share') &&
        this.hasPermission('tradies.files.view')
      )
    },
    canRemoveTradie() {
      return this.hasPermission('tradies.remove')
    },
    canArchiveTradie() {
      return this.hasPermission('tradies.archive')
    },
    canViewTradieProfile() {
      return this.hasPermission('tradies.profile.view')
    },
    canPurchaseTokens() {
      return canBuyTokens(this.user, this.hasPermission)
    },
    canAssignTokens() {
      return (
        (this.user && this.user.is_primary) ||
        this.hasPermission('settings.billing.assign')
      )
    },
    hasSelectableTradiesOnPage() {
      return this.selectableTradiesOnPage.length > 0
    },

    rows() {
      return this.tradiesMeta ? this.tradiesMeta.total : 0
    },

    isAnyTradieSelected() {
      return this.selectedTradies.length > 0
    },

    /** Selectable on current page (accepted + token / access). */
    selectableTradiesOnPage() {
      return (this.tradies || []).filter((t) => this.isTradieSelectable(t))
    },

    selectAllTradiesHeaderLabel() {
      if (this.selectAllTradiesBusy) return 'Loading…'
      const pageIds = this.selectableTradiesOnPage.map((t) => t.id)
      if (
        pageIds.length > 0 &&
        pageIds.every((id) => this.selectedTradies.includes(id))
      ) {
        return 'Deselect all'
      }
      return 'Select all'
    },

    selectAllTradiesHeaderTitle() {
      return 'Select or deselect all tradies matching your current search (all pages)'
    },
    massNotificationByProjectRecipientCount() {
      // TODO: sum tradie counts for selected projects (e.g. from API or cached meta)
      return 0
    },
    massNotificationRecipientCount() {
      if (this.massNotificationTab === 'byIndividuals') {
        return this.massNotificationSelectedUserIds.length
      }
      return this.massNotificationByProjectRecipientCount
    },
    computedFields() {
      if (this.activeTab === 1) {
        return this.applyTradieFieldPermissions([
          {
            key: 'full_name',
            label: 'Name',
            sortable: true,
            formatter: (value) =>
              value == null || String(value).trim() === '' ? '-' : value,
            class: 'w-25',
          },

          {
            key: 'email',
            label: 'Email',
            sortable: true,
            class: 'w-30',
          },

          {
            key: 'trade',
            label: 'Trade',
            sortable: true,
            class: 'w-25',
          },
          {
            key: 'remove',
            label: 'Remove',
            class: 'w-10',
          },

          {
            key: 'archive',
            label: 'Archive',
            class: 'w-10',
          },
          // 'archive',
        ])
      } else
        return this.applyTradieFieldPermissions([
          {
            key: 'full_name',
            label: 'Name',
            sortable: true,
            formatter: (value) => (value === null ? '-' : value),
          },

          {
            key: 'email',
            label: 'Email',
            sortable: true,
          },

          {
            key: 'trade',
            label: 'Trade',
            sortable: true,
          },
          {
            key: 'phone_number',
            label: 'Phone Number',
            sortable: true,
            formatter: (value) => (value === null ? '-' : value),
          },
          {
            key: 'project',
            label: 'Project',
            formatter: (value) => (value === null ? ['-'] : value),
          },
          // Free tier / trial companies skip the token economy entirely, so
          // there's no expiry to show — omit the column for them.
          ...(this.isFree
            ? []
            : [
                {
                  key: 'token_expiry',
                  label: 'Token Expiry',
                  sortable: false,
                },
              ]),
          'remove',
          {
            key: 'select',
            label: 'Select',
            thClass: 'text-center company-tradie-col-select',
            tdClass: 'text-center company-tradie-col-select',
          },
          'archive',
        ])
    },
  },

  watch: {
    currentPage: async function (newCurrentPage) {
      this.currentPage = newCurrentPage
      await this.fetchTradies()
    },

    search: async function (newSearch) {
      this.search = newSearch
      this.currentPage = 1
      await this.fetchTradies()
    },

    activeTab(newtab) {
      this.currentPage = 1
      this.selectedTradies = []
      this.fetchTradies()
    },

    '$route.query.openMassNotification': {
      immediate: true,
      handler(val) {
        if (val === '1') {
          if (
            this.hasPermission('notifications.send') &&
            (this.hasPermission('tradies.view') ||
              this.hasPermission('projects.view'))
          ) {
            this.shareMassNotification(true)
          }
          const query = { ...this.$route.query }
          delete query.openMassNotification
          this.$router.replace({ query }).catch(() => {})
        }
      },
    },
  },

  methods: {
    ...mapActions('company', {
      getTradies: 'getTradies',
      getTradie: 'getTradie',
      addTradie: 'addTradie',
      importTradieCSV: 'importTradieCSV',
      removeTradie: 'removeTradie',
      resendInvitation: 'resendInvitation',
      archiveTradie: 'archiveTradie',
    }),

    ...mapActions('token', {
      purchase: 'purchase',
      checkCanAccessAction: 'checkCanAccess',
      assign: 'assign',
      getStatus: 'getStatus',
    }),

    async archive(item) {
      this.isLoading = true
      try {
        const isArchived = this.activeTab === 0
        await this.archiveTradie({ tradieId: item.id, isArchived: isArchived })

        await this.fetchTradies()
      } catch (err) {
        this.handleError(err)
      } finally {
        this.isLoading = false
      }
    },

    shortenString(arrayString, limit = 25) {
      if (!arrayString) return '-'

      const string = Array.isArray(arrayString)
        ? arrayString.join(', ')
        : arrayString
      return string.length > limit ? string.substring(0, limit) + '...' : string
    },

    getTooltipText(arrayString) {
      if (!arrayString) return '-'
      return Array.isArray(arrayString)
        ? arrayString.join < '<br/>'
        : arrayString
    },

    formatExpiryDate(dateString) {
      if (!dateString) return '-'
      return new Date(dateString).toLocaleDateString('en-AU', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    },

    isExpiringSoon(expiryDate, days) {
      if (!expiryDate) return false
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const expiry = new Date(expiryDate)
      expiry.setHours(0, 0, 0, 0)
      const diffDays = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24))
      return diffDays >= 0 && diffDays <= days
    },

    checkIfAccepted(item) {
      return item.accepted
    },

    isPendingInvitation(tradie) {
      return tradie && [0, null].includes(tradie.accepted)
    },

    // Only set once the BA has actually pressed "Resend Invite" — the
    // original invite alone never populates invite_resent_at.
    hasBeenResent(tradie) {
      return !!(tradie && tradie.invite_resent_at)
    },

    resendInviteTooltip(tradie) {
      return this.hasBeenResent(tradie)
        ? `Last resent: ${dayjs(tradie.invite_resent_at).format('DD/MM/YY')}`
        : 'Resend invitation'
    },

    isTradieSelectable(tradie) {
      return !!(tradie && this.checkIfAccepted(tradie) && tradie.can_access)
    },

    buildCompanyTradiesQuery(page, limit) {
      const params = new URLSearchParams()
      params.set('limit', String(limit))
      params.set('page', String(page))
      params.set('search', this.search || '')
      params.set('archived_only', this.activeTab === 1 ? 'true' : 'false')
      if (this.sortBy) {
        params.set('sort_by', this.sortBy)
        params.set('sort_dir', this.sortDir || 'ASC')
      }
      return params
    },

    selectableIdsFromTradiesResponse(res) {
      const data = res && res.data
      const rows = Array.isArray(data && data.data) ? data.data : []
      return rows
        .filter((t) => this.isTradieSelectable(t))
        .map((t) => t.id)
        .filter((id) => id != null)
    },

    async fetchAllSelectableTradieIds() {
      const meta = this.tradiesMeta || {}
      const total = meta.total != null ? meta.total : 0
      if (!total) return []

      const pageSize = 100
      const lastPage =
        meta.last_page != null
          ? meta.last_page
          : Math.max(1, Math.ceil(total / pageSize))

      const tasks = []
      for (let p = 1; p <= lastPage; p++) {
        const qs = this.buildCompanyTradiesQuery(p, pageSize)
        tasks.push(this.$api.get(`companies/tradies?${qs.toString()}`))
      }

      const results = await Promise.all(tasks)
      const ids = []
      results.forEach((res) => {
        ids.push(...this.selectableIdsFromTradiesResponse(res))
      })
      return [...new Set(ids)]
    },

    async selectAllTradies() {
      if (this.selectAllTradiesBusy || this.isLoading || this.activeTab === 1) {
        return
      }

      this.selectAllTradiesBusy = true
      try {
        const allIds = await this.fetchAllSelectableTradieIds()
        if (!allIds.length) return

        const allSelected = allIds.every((id) =>
          this.selectedTradies.includes(id),
        )

        if (allSelected) {
          const remove = new Set(allIds)
          this.selectedTradies = this.selectedTradies.filter(
            (id) => !remove.has(id),
          )
        } else {
          allIds.forEach((id) => {
            if (!this.selectedTradies.includes(id)) {
              this.selectedTradies.push(id)
            }
          })
        }
      } catch (e) {
        console.error(e)
        this.showToast(
          'Error',
          'Could not load all tradies for selection. Please try again.',
          'danger',
        )
      } finally {
        this.selectAllTradiesBusy = false
      }
    },

    selectTradie(status, tradieId = null) {
      if (!this.selectedTradies.includes(tradieId)) {
        this.selectedTradies.push(tradieId)
      }
    },

    deselectTradie(status, tradieId = null) {
      this.selectedTradies = this.selectedTradies.filter(
        (id) => id !== tradieId,
      )
    },

    toggleTradieSelection(tradieId) {
      if (this.isTradieSelected(tradieId)) {
        this.deselectTradie(true, tradieId)
      } else {
        this.selectTradie(true, tradieId)
      }
    },

    isTradieSelected(tradieId) {
      return this.selectedTradies.includes(tradieId)
    },

    async canAccessTradie() {
      // await check if hey have token
      await this.checkCanAccessAction(this.tradieId).catch((err) =>
        this.handleError(err),
      )
    },

    // Show modal for assign
    assigningToken(item) {
      this.tradieToAssign = item
      this.isAssigningToken = true
    },
    async onAssignToken() {
      if (!this.tradieToAssign) {
        return
      }
      // get company id
      const companyId = this.user.company_id

      if (!companyId) {
        this.showToast('Error', 'Company ID not found', 'danger')
        return
      }
      // isLoading true
      this.isLoading = true
      try {
        // try and await this
        await this.assign(this.tradieToAssign.id, companyId)
        // show toast
        this.showToast('Success', 'Assigning token to tradie')
        // set assign to false
        this.isAssigningToken = false
        // await fetch trdies
        await this.fetchTradies()
        // awwait $store.dispatch token balance
        await this.$store.dispatch('token/getBalance')
      } catch (err) {
        this.handleError(err)
      } finally {
        this.isLoading = false
      }
    },

    // Show modal
    purchasingToken(status) {
      if (!status) {
        this.tokenQuantity = 1
      }
      this.isPurchasingToken = status
    },

    async onPurchaseTokens(quantity) {
      if (!quantity || quantity < 1) {
        return
      }
      await this.purchase(quantity).catch((err) => this.handleError(err))
      this.purchasingToken(false)
    },

    onAssignModalClose() {
      this.isAssigningToken = false
      this.tradieToAssign = null
    },

    onAssignModalPurchaseTokens() {
      this.isAssigningToken = false
      if (this.canPurchaseTokens) {
        this.purchasingToken(true)
      }
    },

    // Resend inv
    async onResendInvitation() {
      if (!this.tradieToAssign || !this.tradieToAssign.id) {
        this.showToast('Error', 'Tradie not found', 'danger')
        return
      }

      this.isLoading = true
      try {
        await this.resendInvitation(this.tradieToAssign.id)
        this.showToast('Success', 'Invitation resent successfully!')
      } catch (err) {
        this.handleError(err)
      } finally {
        this.isLoading = false
      }
    },

    async resendInvitationDirectly(item) {
      if (!item || !item.id) {
        this.showToast('Error', 'Tradie not found', 'danger')
        return
      }

      this.isLoading = true
      try {
        await this.resendInvitation(item.id)
        this.showToast('Success', 'Invitation resent successfully!')
        await this.fetchTradies() // Refresh the list
      } catch (err) {
        this.handleError(err)
      } finally {
        this.isLoading = false
      }
    },
    shareSelectedTradies(status) {
      this.isShareSelectedTradies = status
    },

    async shareMassNotification(status) {
      if (status) {
        await this.$refs.massNotificationModal.prepareForOpen()
      }
      this.isShareMassNotification = !!status
    },

    onMassNotificationSent(payload) {
      const droppedReason = payload && payload.droppedAttachmentReason
      if (droppedReason) {
        this.showToast(
          'Notification sent',
          `Notification sent, but an attachment could not be included: ${droppedReason}`,
          'warning',
        )
      } else {
        this.showToast('Success', 'Notification sent.')
      }
      this.fetchTradies()
    },

    async fetchTradies() {
      this.isLoading = true
      this.tokenExpiryDates = {}
      await this.getTradies({
        page: this.currentPage,
        limit: this.perPage,
        search: this.search,
        sortBy: this.sortBy,
        sortDir: this.sortDir,
        archivedOnly: this.activeTab === 1,
      })
        .catch(() => {})
        .finally(() => {
          this.isLoading = false
        })

      applyTokenExpiryFromListTradies(this, this.tradies)
    },

    async onSortChanged(sort) {
      this.sortBy = sort.sortBy
      this.sortDir = sort.sortDesc ? 'DESC' : 'ASC'

      await this.fetchTradies()
    },

    addingTradie(status) {
      if (status) this.resetData()
      else this.fetchTradies()

      this.isAddingTradie = status
    },

    async onAddTradie() {
      if (!this.tradieEmail.length) return
      this.isLoading = true
      this.errors = null
      try {
        const results = await Promise.all(
          this.tradieEmail.map((email) => this.addTradie(email)),
        )
        this.tradieEmail = []
        this.addingTradie(false)

        let added = 0
        let invites = 0
        for (const r of results) {
          const msg = r && typeof r.message === 'string' ? r.message.trim() : ''
          if (msg === ADD_TRADIE_INVITE_ONLY_MESSAGE) invites += 1
          else added += 1
        }

        if (invites && !added) {
          this.showToast(
            'Invitation sent',
            invites === 1
              ? ADD_TRADIE_INVITE_ONLY_MESSAGE
              : `${invites} invitations have been sent.`,
            'info',
          )
        } else if (added && !invites) {
          const firstMsg =
            results[0] && typeof results[0].message === 'string'
              ? results[0].message.trim()
              : ''
          this.showToast(
            'Success',
            added === 1
              ? firstMsg || ADD_TRADIE_NEW_ACCOUNT_MESSAGE
              : `${added} tradies have been added successfully!`,
            'success',
          )
        } else if (added && invites) {
          this.showToast(
            'Done',
            `${added} added, ${invites} invitation(s) sent (pending acceptance).`,
            'warning',
          )
        }
      } catch (e) {
        this.errors = e.errors
      } finally {
        this.isLoading = false
      }
    },

    importingTradieCSV(status) {
      this.tradieImportFileClientHint = null
      this.isImportingTradieCSV = status
    },

    onTradieImportFileChange() {
      this.tradieImportFileClientHint = null
      this.$nextTick(() => {
        const file = this.tradieCsvFile
        if (!file) return
        const fileCheck = validateTradieImportFile(file)
        if (!fileCheck.ok) {
          this.tradieCsvFile = null
          if (fileCheck.message === TRADIE_IMPORT_INVALID_TYPE_MESSAGE) {
            this.tradieImportFileClientHint = fileCheck.message
            this.showToast('Invalid file', fileCheck.message, 'danger')
          } else {
            this.showToast('Import', fileCheck.message, 'danger')
          }
        }
      })
    },

    async onImportTradieCSV() {
      const fileCheck = validateTradieImportFile(this.tradieCsvFile)
      if (!fileCheck.ok) {
        if (fileCheck.message === TRADIE_IMPORT_INVALID_TYPE_MESSAGE) {
          this.tradieImportFileClientHint = fileCheck.message
        }
        const title =
          fileCheck.message === TRADIE_IMPORT_INVALID_TYPE_MESSAGE
            ? 'Invalid file'
            : 'Import'
        this.showToast(title, fileCheck.message, 'danger')
        return
      }

      this.isLoading = true
      try {
        const result = await this.importTradieCSV({
          tradieCsvFile: this.tradieCsvFile,
        })
        this.fetchTradies()
        this.importingTradieCSV(false)

        if (!result || result.kind === 'full_success') {
          this.showToast('Success', 'Tradies Added!')
        } else if (result.kind === 'partial_success') {
          const msg = (result.message || '').trim()
          const invitesOnly = msg === CSV_IMPORT_MSG_INVITES_ONLY
          const toastTitle = invitesOnly
            ? 'Invitations sent'
            : 'Import finished'
          const toastBody =
            msg ||
            (invitesOnly
              ? 'Invitations were sent to existing accounts.'
              : `${result.importedCount} tradie(s) imported. Some rows were skipped.`)
          this.showToast(
            toastTitle,
            toastBody,
            invitesOnly ? 'info' : 'warning',
          )
          this.importCsvErrorModalVariant = 'partial_success'
          this.importCsvErrorSummary =
            result.message ||
            `${result.importedCount} tradie(s) imported. Some email addresses were skipped.`
          this.importCsvErrorEmails = []
          this.importCsvErrorImportedEmails = result.importedEmails || []
          this.importCsvErrorInvitedEmails = result.invitedEmails || []
          this.importCsvErrorSkippedAlreadyInCompany =
            result.skippedAlreadyInCompany
          this.importCsvErrorSkippedBusinessAdmins =
            result.skippedBusinessAdmins
          this.showImportCsvErrorModal = true
        }
      } catch (err) {
        const fileValidationMsg = getTradieImportCsvFileValidationMessage(err)
        const mimeOnly =
          fileValidationMsg &&
          isTradieImportCsvFileMimeValidationMessage(fileValidationMsg)

        if (mimeOnly) {
          this.showToast('Import', fileValidationMsg, 'danger')
          return
        }

        const parsed = parseTradieCsvImport422(err)
        if (parsed) {
          const genericSummary =
            !parsed.summary ||
            /^the given data was invalid\.?$/i.test(parsed.summary.trim())
          const summary =
            fileValidationMsg && !mimeOnly
              ? fileValidationMsg
              : genericSummary && fileValidationMsg
              ? fileValidationMsg
              : parsed.summary

          this.importCsvErrorModalVariant = 'failure'
          this.importCsvErrorSummary = summary
          this.importCsvErrorEmails = parsed.emails
          this.importCsvErrorImportedEmails = parsed.importedEmails || []
          this.importCsvErrorInvitedEmails = parsed.invitedEmails || []
          this.importCsvErrorSkippedAlreadyInCompany =
            parsed.skippedAlreadyInCompany
          this.importCsvErrorSkippedBusinessAdmins =
            parsed.skippedBusinessAdmins
          this.showImportCsvErrorModal = true
        } else {
          this.handleError(err)
        }
      } finally {
        this.isLoading = false
      }
    },

    showToast(title, message, variant = 'success') {
      this.$bvToast.toast(message, {
        title: title,
        autoHideDelay: 3000,
        variant: variant,
      })
    },

    /** Drops the remove/archive columns when the role lacks those permissions. */
    applyTradieFieldPermissions(fields) {
      return fields.filter((f) => {
        const key = typeof f === 'string' ? f : f.key
        if (key === 'remove' && !this.canRemoveTradie) return false
        if (key === 'archive' && !this.canArchiveTradie) return false
        return true
      })
    },

    async viewTradie(item, i) {
      if (!this.canViewTradieProfile) return

      if (!item.can_access) {
        if (this.canAssignTokens) {
          this.assigningToken(item)
        }
        return
      }

      // 0 = declined, null = pending
      if ([0, null].includes(item.accepted)) {
        this.showDeclinedModal = true
        return
      }

      // Navigate to tradie details
      this.$router.push({
        name: 'tradies.view-tradie',
        params: {
          tradieId: item.id,
        },
      })
    },

    removingTradie(status, tradieId = null) {
      this.isRemovingTradie = status

      if (status) {
        this.tradieId = tradieId
        const tradie = this.tradies.find((x) => x.id === tradieId)
        this.tradieName = tradie.joined ? tradie.full_name : tradie.email
      }
    },

    async onRemoveTradie() {
      this.isLoading = true

      this.removeTradie(this.tradieId)
        .then(() => {
          this.fetchTradies()
          this.showToast('Success', 'Tradies Removed!')
          this.removingTradie(false)
        })
        .catch((err) => this.handleError(err))
        .finally(() => (this.isLoading = false))
    },

    resetData() {
      this.errors = null
      this.tradieEmail = null
    },

    handleError(err) {
      const msg =
        (err && err.data && err.data.message) ||
        (err && err.message) ||
        'Something went wrong. Please try again.'
      this.showToast('Oops.. Something went wrong!', msg, 'danger')
    },

    getRowClass(item) {
      if (!item) return 'cursor-pointer'

      const baseClass = 'cursor-pointer'
      if (!item.can_access) {
        return `${baseClass} row-locked`
      }
      return baseClass
    },
  },

  async mounted() {
    await this.fetchTradies()
    await this.$store.dispatch('token/getBalance')
  },
}
</script>

<style lang="scss" scoped>
/deep/ .cursor-pointer {
  cursor: pointer;
}

.text-noproject {
  color: lightgray;
}

/deep/ .excluded .custom-file-label {
  height: 69%;
}

.hover-bg-darken:hover {
  background-color: #f0f0f0;
  transition: background-color 0.2s;
}

/deep/ .company-tradie-col-select {
  min-width: 9.5rem;
  width: 9.5rem;
  white-space: nowrap;
}

.tradie-btn-row {
  gap: 0.5rem;
}

.tradie-action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.mobile-tradie-item {
  padding: 0.65rem 1rem;
  border-bottom: 1px solid #e1e6ea;
  background: white;
}

// Matches the desktop table's locked-row background (see .row-locked in
// _table.scss) so a locked tradie looks the same whether you're on the
// table or this compact mobile list.
.mobile-tradie-item--locked {
  background-color: #f5f5f5;
}

.mobile-tradie__name {
  font-weight: 600;
  font-size: 0.875rem;
  color: #364052;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mobile-tradie__lock-icon {
  font-size: 0.7rem;
  color: #8b95a3;
  flex-shrink: 0;
}

.mobile-tradie__meta {
  font-size: 0.72rem;
  color: #8b95a3;
  margin-top: 0.15rem;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mobile-tradie__actions {
  gap: 0.3rem;
  flex-shrink: 0;
  margin-left: 0.5rem;
}

.mobile-tradie__action-btn {
  height: 28px;
  width: 28px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  flex-shrink: 0;
}

.mobile-tradie__select-btn {
  border: 1px solid grey;
}

// The global .btn rule (height: 56px; padding: 4px 32px) wins over
// bootstrap's size="sm" here, which is why these were still rendering
// large despite `size="sm"`. This row only ever shows below md (see
// d-md-none above), so no media query is needed to scope it to mobile.
.tradie-action-btn {
  height: 42px !important;
  width: 42px !important;
  padding: 0 !important;
}

@media (max-width: 767.98px) {
  /deep/ .tradie-pagination {
    .page-link {
      padding: 0.4rem 0.75rem;
      font-size: 0.9rem;
    }

    .page-item:first-child .page-link,
    .page-item:last-child .page-link {
      padding: 0.4rem 0.6rem;
    }
  }
}

// Small-phone-only tightening of the "Remove Tradie" confirmation modal,
// matching the same treatment used for the other delete-style modals in
// the app. Scoped to this media query only, so 576px and up is unchanged.
@media (max-width: 575px) {
  /deep/ .remove-tradie-modal {
    .modal-dialog {
      margin: 0.75rem;
    }

    .modal-header {
      padding-bottom: 0;
    }

    .modal-title {
      font-size: 1.15rem;
    }

    .modal-body {
      padding: 0 0.5rem;
    }
  }

  .remove-tradie-modal-text {
    margin: 0.4rem 0 0.75rem !important;
    font-size: 0.9rem;
  }

  // Spread CANCEL/YES evenly across the full footer width instead of
  // leaving them bunched on the right with empty space beside them.
  /deep/ .remove-tradie-modal .modal-footer {
    justify-content: stretch !important;
    gap: 0.5rem;
  }

  .remove-tradie-modal-btn {
    height: 44px !important;
    padding: 4px 20px !important;
    flex: 1 1 0;
    min-width: 0;
  }

  // Same treatment for the "Add Tradie" modal.
  /deep/ .add-tradie-modal {
    .modal-dialog {
      margin: 0.75rem;
    }

    .modal-header {
      padding-bottom: 0;
    }

    .modal-title {
      font-size: 1.15rem;
    }

    .modal-body {
      padding: 0 0.5rem;
    }
  }

  // The buttons wrapped (CANCEL on its own line, ADD TRADIE stretching
  // full-width on the next) because their combined width didn't fit. Force
  // them to stay on one row and spread evenly across the full width instead
  // of shrinking to content and bunching on the right.
  .add-tradie-modal-footer {
    flex-wrap: nowrap !important;
    justify-content: stretch !important;
    gap: 0.5rem;
  }

  .add-tradie-modal-btn {
    height: 44px !important;
    padding: 4px 14px !important;
    font-size: 0.85rem;
    flex: 1 1 0;
    min-width: 0;
    margin-left: 0 !important;
    margin-right: 0 !important;
  }

  // The email input was getting cut off because the default 15px column
  // gutter plus the form-tags box's own padding left too little room on a
  // 320px screen. Trim both.
  .add-tradie-modal-form-col {
    padding-left: 0.5rem;
    padding-right: 0.5rem;
  }

  // Box was much taller than one line needs — force it back to a compact,
  // content-sized height instead of whatever padding/margin is stacking up
  // internally (form-tags list + field + input all nest inside it).
  /deep/ .add-tradie-modal-tags {
    display: flex !important;
    align-items: center !important;
    min-height: 0 !important;
    height: 50px !important;
    padding: 0 0.5rem !important;
    font-size: 0.9rem;

    .b-form-tags-list {
      margin: 0 !important;
      padding: 0 !important;
      min-height: 0 !important;
      width: 100%;
    }

    .b-form-tags-field {
      margin: 0 !important;
      padding: 0 !important;
    }

    .b-form-tags-input {
      margin: 0 !important;
      padding: 0 !important;
      line-height: 1.4 !important;
      height: auto !important;
    }
  }

  // Same treatment for the "Import tradies" modal.
  /deep/ .import-tradie-modal {
    .modal-dialog {
      margin: 0.75rem;
    }

    .modal-header {
      padding-bottom: 0;
    }

    .modal-title {
      font-size: 1.15rem;
    }

    .modal-body {
      padding: 0 0.5rem;
    }
  }

  // Same CANCEL/UPLOAD wrapping issue as the Add Tradie modal, same fix.
  .import-tradie-modal-footer {
    flex-wrap: nowrap !important;
    justify-content: stretch !important;
    gap: 0.5rem;
  }

  .import-tradie-modal-btn {
    height: 44px !important;
    padding: 4px 14px !important;
    font-size: 0.85rem;
    flex: 1 1 0;
    min-width: 0;
    margin-left: 0 !important;
    margin-right: 0 !important;
  }
}
</style>
